import React from 'react';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Box from '@cloudscape-design/components/box';
import { ModelMetricsAggregated, labelModel } from '../../types';

interface SummaryCardsProps {
  models: Record<string, ModelMetricsAggregated>;
  totalRuns: number;
}

function findBestModel(
  models: Record<string, ModelMetricsAggregated>,
  metric: keyof ModelMetricsAggregated,
  mode: 'max' | 'min'
): ModelMetricsAggregated | null {
  const entries = Object.values(models).filter((m) => (m[metric] as number) > 0);
  if (entries.length === 0) return null;

  return entries.reduce((best, current) => {
    const bestVal = best[metric] as number;
    const curVal = current[metric] as number;
    return mode === 'max'
      ? curVal > bestVal ? current : best
      : curVal < bestVal ? current : best;
  });
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({ models, totalRuns }) => {
  const bestRated = findBestModel(models, 'avgRatingReceived', 'max');
  const fastest = findBestModel(models, 'avgResponseTime', 'min');
  const mostEfficient = findBestModel(models, 'avgTokens', 'min');

  return (
    <ColumnLayout columns={4} variant="text-grid">
      <div>
        <Box variant="awsui-key-label">Total Runs</Box>
        <Box variant="awsui-value-large">{totalRuns}</Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Best Rated Model</Box>
        <Box variant="awsui-value-large">
          {bestRated ? labelModel(bestRated) : 'N/A'}
        </Box>
        {bestRated && (
          <Box color="text-body-secondary">
            Avg Rating: {bestRated.avgRatingReceived.toFixed(1)}
          </Box>
        )}
      </div>
      <div>
        <Box variant="awsui-key-label">Fastest Model</Box>
        <Box variant="awsui-value-large">
          {fastest ? labelModel(fastest) : 'N/A'}
        </Box>
        {fastest && (
          <Box color="text-body-secondary">
            Avg Response: {fastest.avgResponseTime.toFixed(0)} ms
          </Box>
        )}
      </div>
      <div>
        <Box variant="awsui-key-label">Most Token-Efficient</Box>
        <Box variant="awsui-value-large">
          {mostEfficient ? labelModel(mostEfficient) : 'N/A'}
        </Box>
        {mostEfficient && (
          <Box color="text-body-secondary">
            Avg Tokens: {mostEfficient.avgTokens.toFixed(0)}
          </Box>
        )}
      </div>
    </ColumnLayout>
  );
};
